import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ReactTyped } from "react-typed";
import { Check, X, RefreshCw, ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";

const colorCombinations = [
  "bg-purple-200 border-purple-300 text-purple-700 dark:bg-purple-800/40 dark:border-purple-600 dark:text-purple-300",
  "bg-green-200 border-green-300 text-green-700 dark:bg-green-800/40 dark:border-green-600 dark:text-green-300",
  "bg-red-200 border-red-300 text-red-700 dark:bg-red-800/40 dark:border-red-600 dark:text-red-300",
  "bg-blue-200 border-blue-300 text-blue-700 dark:bg-blue-800/40 dark:border-blue-600 dark:text-blue-300",
  "bg-yellow-200 border-yellow-300 text-yellow-700 dark:bg-yellow-800/40 dark:border-yellow-600 dark:text-yellow-300",
];

const baseHighlightClasses = `
  inline-block px-1 mx-0.5 rounded-md
  border
  transition-all duration-200 ease-in-out
`;

export interface CustomPromptInputProps {
  value: string;
  onChange: (value: string) => void;
  highlightedWords: { word: string; alternatives: string[] }[];
  placeholder?: string;
  disabled?: boolean;
  maxLength?: number;
  dataTestId?: string;
  isEnhancing?: boolean;
  showActionButtons?: boolean;
  onApprove?: () => void;
  onReject?: () => void;
  onRegenerate?: () => void;
  onRestore?: () => void;
  isEnhanced?: boolean;
  typewriterStrings?: string[];
}

export const CustomPromptInput: React.FC<CustomPromptInputProps> = ({
  value,
  onChange,
  highlightedWords,
  placeholder,
  disabled = false,
  maxLength = 300,
  dataTestId,
  isEnhancing = false,
  showActionButtons = false,
  onApprove,
  onReject,
  onRegenerate,
  onRestore,
  isEnhanced = false,
  typewriterStrings = [
    "Güzel bir manzara yaratın...",
    "Renkli bir poster tasarlayın...",
    "Yaratıcı bir logo çizin...",
    "Etkileyici bir rozet yapın...",
  ],
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [hasBeenClicked, setHasBeenClicked] = useState(false);
  const [dropdown, setDropdown] = useState<{
    word: string;
    rect: { top: number; left: number };
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const viewRef = useRef<HTMLDivElement>(null);
  const [cursorPosition, setCursorPosition] = useState<number | null>(null);
  const [viewHeight, setViewHeight] = useState<number | null>(null);
  const [clickPosition, setClickPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  // Add click outside handler for dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Capture the height of the view element when content changes
  useEffect(() => {
    if (viewRef.current && !isEditing) {
      const height = viewRef.current.scrollHeight;
      setViewHeight(height);
    }
  }, [value, isEditing]);

  // Adjust height when switching to editing mode or when value changes
  useEffect(() => {
    if (isEditing && textareaRef.current && viewHeight) {
      textareaRef.current.style.height = `${viewHeight}px`;
    }
  }, [isEditing, viewHeight]);

  // Function to position cursor at click position within text
  const calculateCursorPositionFromClick = (
    element: HTMLElement,
    x: number,
    y: number,
  ) => {
    if (!element || !element.textContent) return 0;

    // Create a range to get position information
    const range = document.createRange();
    const selection = window.getSelection();

    // Try to get the exact position where user clicked
    let node: Node | null = null;
    let offset = 0;

    // Use caretPositionFromPoint if available (more accurate)
    if (document.caretPositionFromPoint) {
      const position = document.caretPositionFromPoint(x, y);
      if (position) {
        node = position.offsetNode;
        offset = position.offset;
      }
    }
    // Or use caretRangeFromPoint (alternative method)
    else if (document.caretRangeFromPoint) {
      const caretRange = document.caretRangeFromPoint(x, y);
      if (caretRange) {
        node = caretRange.startContainer;
        offset = caretRange.startOffset;
      }
    }

    // If we found a valid node and it's a text node
    if (node && node.nodeType === Node.TEXT_NODE) {
      // Calculate the text position based on the node and offset
      let totalOffset = offset;

      // We need to add lengths of all preceding text nodes
      const walk = document.createTreeWalker(
        element,
        NodeFilter.SHOW_TEXT,
        null,
      );

      let currentNode = walk.nextNode();
      while (currentNode && currentNode !== node) {
        totalOffset += currentNode.textContent?.length || 0;
        currentNode = walk.nextNode();
      }

      return totalOffset;
    }

    // Fallback to putting cursor at end of text
    return element.textContent.length;
  };

  // Restore cursor position when returning to editing mode
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      if (clickPosition && viewRef.current) {
        // Calculate cursor position from click coordinates
        const calculatedPosition = calculateCursorPositionFromClick(
          viewRef.current,
          clickPosition.x,
          clickPosition.y,
        );

        // Set cursor position based on click
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(
          calculatedPosition,
          calculatedPosition,
        );

        // Reset click position after using it
        setClickPosition(null);
      } else if (cursorPosition !== null) {
        // Use stored cursor position from previous edit
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(cursorPosition, cursorPosition);
      }
    }
  }, [isEditing, cursorPosition, clickPosition]);

  const handleContainerClick = (e: React.MouseEvent) => {
    // Don't process clicks on dropdown
    if ((e.target as HTMLElement).closest(".dropdown-container")) {
      return;
    }

    setDropdown(null);

    if (!disabled && !isEnhancing) {
      // Store the click position relative to the view element
      if (viewRef.current) {
        const rect = viewRef.current.getBoundingClientRect();
        setClickPosition({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        });
      }

      setIsEditing(true);
      setHasBeenClicked(true);
    }
  };

  const handleBlur = () => {
    // Save cursor position before blurring
    if (textareaRef.current) {
      setCursorPosition(textareaRef.current.selectionStart);
    }
    setIsEditing(false);
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    // Save cursor position during typing
    setCursorPosition(e.target.selectionStart);
    if (newValue.length <= maxLength) {
      onChange(newValue);

      // Auto-resize the textarea based on content
      e.target.style.height = "auto";
      e.target.style.height = `${e.target.scrollHeight}px`;
    }
  };

  const replaceWord = (oldWord: string, newWord: string) => {
    const wordGroup = highlightedWords.find(
      (hw) =>
        hw.word.toLowerCase() === oldWord.toLowerCase() ||
        hw.word.toLowerCase() === newWord.toLowerCase() ||
        hw.alternatives.some(
          (alt) =>
            alt.toLowerCase() === oldWord.toLowerCase() ||
            alt.toLowerCase() === newWord.toLowerCase(),
        ),
    );

    if (wordGroup) {
      const allPossibleWords = [wordGroup.word, ...wordGroup.alternatives];
      const regex = new RegExp(`\\b(${allPossibleWords.join("|")})\\b`, "i");
      const newValue = value.replace(regex, newWord);
      onChange(newValue);
      setDropdown(null);
    }
  };

  const getTextSegments = () => {
    const segments: {
      text: string;
      isHighlighted: boolean;
      originalWord?: string;
      alternatives?: string[];
    }[] = [];
    let remainingText = value;

    const allHighlightWords = highlightedWords.reduce(
      (acc, { word, alternatives }) => {
        return [
          ...acc,
          { text: word, originalWord: word, alternatives },
          ...alternatives.map((alt) => ({
            text: alt,
            originalWord: word,
            alternatives,
          })),
        ];
      },
      [] as { text: string; originalWord: string; alternatives: string[] }[],
    );

    allHighlightWords.sort((a, b) => b.text.length - a.text.length);

    while (remainingText.length > 0) {
      let matched = false;

      for (const { text, originalWord, alternatives } of allHighlightWords) {
        const regex = new RegExp(`\\b${text}\\b`, "i");
        const match = remainingText.match(regex);

        if (match && match.index === 0) {
          segments.push({
            text: match[0],
            isHighlighted: true,
            originalWord,
            alternatives,
          });
          remainingText = remainingText.slice(match[0].length);
          matched = true;
          break;
        }
      }

      if (!matched) {
        const nextHighlight = allHighlightWords
          .map((hw) => {
            const regex = new RegExp(`\\b${hw.text}\\b`, "i");
            const match = remainingText.match(regex);
            return match
              ? { index: match.index, length: match[0].length }
              : null;
          })
          .filter(Boolean)
          .sort((a, b) => (a?.index || 0) - (b?.index || 0))[0];

        if (nextHighlight) {
          segments.push({
            text: remainingText.slice(0, nextHighlight.index),
            isHighlighted: false,
          });
          remainingText = remainingText.slice(nextHighlight.index);
        } else {
          segments.push({
            text: remainingText,
            isHighlighted: false,
          });
          remainingText = "";
        }
      }
    }

    return segments;
  };

  // Calculate max height based on content
  const maxContainerHeight = 245; // Fixed height as per requirement

  // Set up a ref to measure content height
  const contentMeasureRef = useRef<HTMLDivElement>(null);

  // Effect to measure and sync heights on content change
  useEffect(() => {
    const measureContent = () => {
      if (contentMeasureRef.current) {
        const measuredHeight = contentMeasureRef.current.scrollHeight;
        setViewHeight(measuredHeight > 245 ? measuredHeight : 245);
      }
    };

    // Measure on content change
    measureContent();

    // Set up a resize observer to handle content changes
    if (contentMeasureRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        measureContent();
      });

      resizeObserver.observe(contentMeasureRef.current);
      return () => resizeObserver.disconnect();
    }
  }, [value]);

  // Common style object for consistent dimensions between view and edit mode
  const commonContainerStyle = {
    minHeight: "170px",
    height: "auto",
    maxHeight: `${maxContainerHeight}px`,
    position: "relative" as const,
  };

  const commonContentStyles = {
    padding: "1rem",
    fontSize: "1rem",
    lineHeight: "1.5",
    width: "100%",
    height: "100%",
    overflowWrap: "break-word", // Ensure text wraps properly
    wordBreak: "break-word", // Additional word break control
  };

  return (
    <div ref={containerRef} className="relative w-full imagen-prompt-input">
      <div
        data-testid="imagen-prompt-div"
        className={`relative transition-all duration-200 ${
          disabled || isEnhancing ? "opacity-50 cursor-not-allowed" : ""
        }`}
        onClick={handleContainerClick}
        style={commonContainerStyle}
      >
        {/* Hidden div to measure content height */}
        <div
          ref={contentMeasureRef}
          className="absolute opacity-0 pointer-events-none w-full whitespace-pre-wrap"
          style={{
            padding: "1rem",
            fontSize: "1rem",
            lineHeight: "1.5",
          }}
        >
          {value || placeholder || ""}
        </div>

        {isEditing ? (
          <div className="w-full h-full relative" style={commonContainerStyle}>
            <textarea
              ref={textareaRef}
              value={value}
              onChange={handleTextareaChange}
              onBlur={handleBlur}
              placeholder={placeholder}
              disabled={disabled || isEnhancing}
              maxLength={maxLength}
              className={`w-full h-full scrollbar bg-transparent resize-none outline-none border-none text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 inset-0
                ${
                  isEnhancing
                    ? "opacity-50 cursor-not-allowed pointer-events-none"
                    : ""
                }`}
              style={{
                boxShadow: "none",
                ...commonContentStyles,
                minHeight: "100%",
                maxHeight: "220px",
                overflowY: "auto",
                overflowWrap: "break-word",
                wordBreak: "break-word",
              }}
              data-testid={dataTestId}
            />
          </div>
        ) : (
          <div
            ref={viewRef}
            className="w-full h-full overflow-y-auto text-gray-900 dark:text-white whitespace-pre-wrap scrollbar"
            style={{
              ...commonContentStyles,
              maxHeight: `220px`,
              overflowY: "auto",
              overflowX: "hidden",
              overflowWrap: "break-word",
              wordBreak: "break-word",
            }}
          >
            {value ? (
              getTextSegments().map((segment, index) =>
                segment.isHighlighted ? (
                  <span
                    key={index}
                    className={`${baseHighlightClasses}
                      ${colorCombinations[index % colorCombinations.length]}
                      cursor-pointer`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (dropdown?.word === segment.originalWord) {
                        setDropdown(null);
                        return;
                      }
                      const rect = e.currentTarget.getBoundingClientRect();
                      const container =
                        containerRef.current?.getBoundingClientRect();
                      if (container && segment.originalWord) {
                        setDropdown({
                          word: segment.originalWord,
                          rect: {
                            top: rect.bottom - container.top,
                            left: rect.left - container.left,
                          },
                        });
                      }
                    }}
                  >
                    {segment.text} ▼
                  </span>
                ) : (
                  <span key={index}>{segment.text}</span>
                ),
              )
            ) : (
              <div className="text-gray-400 dark:text-gray-500 text-base pointer-events-none">
                {!hasBeenClicked && !disabled && !isEnhancing ? (
                  <ReactTyped
                    strings={typewriterStrings}
                    typeSpeed={40}
                    backSpeed={30}
                    backDelay={2000}
                    loop
                    showCursor={true}
                    cursorChar="|"
                    className="text-gray-400 dark:text-gray-500"
                  />
                ) : (
                  placeholder
                )}
              </div>
            )}
          </div>
        )}

        <AnimatePresence>
          {isEnhancing && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 rounded-xl overflow-hidden backdrop-blur-md z-20"
            >
              <motion.div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full opacity-80"
                animate={{
                  scale: [1, 1.2, 1],
                  background: [
                    "conic-gradient(at center, #FF61D8, #6B8DE3, #39E5B6, #FCE53D, #FF61D8)",
                    "conic-gradient(at center, #6B8DE3, #39E5B6, #FCE53D, #FF61D8, #6B8DE3)",
                    "conic-gradient(at center, #39E5B6, #FCE53D, #FF61D8, #6B8DE3, #39E5B6)",
                    "conic-gradient(at center, #FCE53D, #FF61D8, #6B8DE3, #39E5B6, #FCE53D)",
                  ],
                  rotate: [0, 360],
                }}
                style={{
                  maskImage:
                    "radial-gradient(circle at center, black 30%, transparent 70%)",
                  WebkitMaskImage:
                    "radial-gradient(circle at center, black 30%, transparent 70%)",
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />

              <motion.div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 rounded-full opacity-60"
                animate={{
                  scale: [1.2, 0.8, 1.2],
                  rotate: [360, 0],
                  background: [
                    "radial-gradient(circle, #FF61D8 0%, transparent 70%)",
                    "radial-gradient(circle, #6B8DE3 0%, transparent 70%)",
                    "radial-gradient(circle, #39E5B6 0%, transparent 70%)",
                  ],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />

              <motion.div
                className="absolute inset-0 backdrop-blur-[12px]"
                animate={{
                  background: [
                    "radial-gradient(circle at center, rgba(255,255,255,0.1) 30%, rgba(255,255,255,0.05) 70%)",
                    "radial-gradient(circle at center, rgba(255,255,255,0.05) 30%, rgba(255,255,255,0.1) 70%)",
                  ],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />

              <motion.div
                className="absolute w-32 h-32 rounded-full bg-gradient-to-r from-fuchsia-500/40 to-purple-600/40 blur-xl"
                animate={{
                  x: ["0%", "60%", "0%"],
                  y: ["0%", "60%", "0%"],
                  scale: [1, 1.8, 1],
                  rotate: [0, 360, 0],
                }}
                transition={{
                  duration: 8,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
              <motion.div
                className="absolute w-40 h-40 rounded-full bg-gradient-to-r from-cyan-400/40 to-blue-600/40 blur-xl"
                animate={{
                  x: ["80%", "20%", "80%"],
                  y: ["20%", "80%", "20%"],
                  scale: [1.5, 0.5, 1.5],
                  rotate: [180, -180, 180],
                }}
                transition={{
                  duration: 6,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
              <motion.div
                className="absolute w-36 h-36 rounded-full bg-gradient-to-r from-amber-400/40 to-orange-600/40 blur-xl"
                animate={{
                  x: ["40%", "90%", "40%"],
                  y: ["90%", "30%", "90%"],
                  scale: [0.5, 2, 0.5],
                  rotate: [45, 315, 45],
                }}
                transition={{
                  duration: 7,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />

              {[...Array(16)].map((_, i) => (
                <motion.div
                  key={i}
                  className={`absolute w-3 h-3 rounded-full ${
                    [
                      "bg-pink-400/90",
                      "bg-purple-400/90",
                      "bg-blue-400/90",
                      "bg-green-400/90",
                      "bg-yellow-400/90",
                      "bg-orange-400/90",
                      "bg-red-400/90",
                      "bg-indigo-400/90",
                    ][i % 8]
                  }`}
                  initial={{
                    x: `${Math.random() * 100}%`,
                    y: `${Math.random() * 100}%`,
                    scale: 0,
                  }}
                  animate={{
                    x: [
                      `${Math.random() * 100}%`,
                      `${Math.random() * 100}%`,
                      `${Math.random() * 100}%`,
                    ],
                    y: [
                      `${Math.random() * 100}%`,
                      `${Math.random() * 100}%`,
                      `${Math.random() * 100}%`,
                    ],
                    scale: [0, 1.8, 0],
                    rotate: [0, 360, 720],
                  }}
                  transition={{
                    duration: 1.5 + Math.random() * 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: i * 0.1,
                  }}
                />
              ))}

              <motion.div
                className="absolute inset-0"
                animate={{
                  background: [
                    "linear-gradient(45deg, transparent, rgba(255,105,180,0.3), transparent)",
                    "linear-gradient(135deg, transparent, rgba(147,112,219,0.3), transparent)",
                    "linear-gradient(225deg, transparent, rgba(64,224,208,0.3), transparent)",
                    "linear-gradient(315deg, transparent, rgba(255,215,0,0.3), transparent)",
                  ],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />

              <motion.div
                className="absolute inset-0"
                animate={{
                  x: ["-100%", "100%"],
                  background: [
                    "linear-gradient(to right, transparent, rgba(255,255,255,0.4), transparent)",
                    "linear-gradient(to right, transparent, rgba(255,255,255,0.2), transparent)",
                  ],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />

              <motion.div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-full bg-white/20"
                animate={{
                  scale: [0, 1.5, 0],
                  opacity: [0, 0.5, 0],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {dropdown && (
          <div
            className="absolute z-10 min-w-[120px]
              bg-white dark:bg-gray-800
              rounded-lg shadow-lg
              border border-purple-200 dark:border-purple-700
              p-1 backdrop-blur-sm
              animate-fadeIn dropdown-container"
            style={{
              top: `${dropdown.rect.top + 5}px`,
              left: `${dropdown.rect.left}px`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {highlightedWords
              .find(
                (hw) => hw.word.toLowerCase() === dropdown.word.toLowerCase(),
              )
              ?.alternatives.map((alt, i) => (
                <div
                  key={i}
                  className="m-1 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    replaceWord(dropdown.word, alt);
                  }}
                >
                  <span
                    className={`${baseHighlightClasses}
                      ${colorCombinations[i % colorCombinations.length]}
                      hover:scale-[1.02] hover:shadow-sm
                      active:scale-[0.98]
                      w-full block`}
                  >
                    {alt}
                  </span>
                </div>
              ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {showActionButtons && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.9 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="flex items-center justify-center gap-3 mt-4"
          >
            <motion.div
              className="flex justify-center items-center w-12 h-12 bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl shadow-lg cursor-pointer group border border-white/40 dark:border-gray-700/40 relative overflow-hidden"
              onClick={onApprove}
              whileHover={{
                scale: 1.1,
                boxShadow: "0 8px 25px rgba(34,197,94,0.2)",
                transition: { duration: 0.2 },
              }}
              whileTap={{ scale: 0.9 }}
            >
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-green-400/20 via-emerald-400/20 to-teal-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                animate={{
                  background: [
                    "linear-gradient(45deg, rgba(34,197,94,0.1), rgba(16,185,129,0.1), rgba(20,184,166,0.1))",
                    "linear-gradient(135deg, rgba(16,185,129,0.1), rgba(20,184,166,0.1), rgba(34,197,94,0.1))",
                    "linear-gradient(225deg, rgba(20,184,166,0.1), rgba(34,197,94,0.1), rgba(16,185,129,0.1))",
                  ],
                }}
                transition={{ duration: 2, repeat: Infinity }}
              />
              <Check className="w-5 h-5 text-green-600 dark:text-green-400 group-hover:scale-110 transition-transform duration-300 relative z-10" />

              <motion.div
                className="absolute w-1 h-1 bg-green-400 rounded-full opacity-0 group-hover:opacity-100"
                animate={{
                  y: [0, -10, 0],
                  x: [-5, 5, -5],
                  scale: [0, 1, 0],
                }}
                transition={{ duration: 1.5, repeat: Infinity, delay: 0.2 }}
                style={{ top: "20%", left: "20%" }}
              />
              <motion.div
                className="absolute w-1 h-1 bg-emerald-400 rounded-full opacity-0 group-hover:opacity-100"
                animate={{
                  y: [0, -8, 0],
                  x: [5, -5, 5],
                  scale: [0, 1, 0],
                }}
                transition={{ duration: 1.5, repeat: Infinity, delay: 0.4 }}
                style={{ top: "70%", right: "20%" }}
              />
            </motion.div>

            <motion.div
              className="flex justify-center items-center w-12 h-12 bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl shadow-lg cursor-pointer group border border-white/40 dark:border-gray-700/40 relative overflow-hidden"
              onClick={onReject}
              whileHover={{
                scale: 1.1,
                boxShadow: "0 8px 25px rgba(239,68,68,0.2)",
                transition: { duration: 0.2 },
              }}
              whileTap={{ scale: 0.9 }}
            >
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-red-400/20 via-rose-400/20 to-pink-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                animate={{
                  background: [
                    "linear-gradient(45deg, rgba(239,68,68,0.1), rgba(244,63,94,0.1), rgba(236,72,153,0.1))",
                    "linear-gradient(135deg, rgba(244,63,94,0.1), rgba(236,72,153,0.1), rgba(239,68,68,0.1))",
                    "linear-gradient(225deg, rgba(236,72,153,0.1), rgba(239,68,68,0.1), rgba(244,63,94,0.1))",
                  ],
                }}
                transition={{ duration: 2, repeat: Infinity }}
              />
              <X className="w-5 h-5 text-red-600 dark:text-red-400 group-hover:rotate-90 transition-transform duration-300 relative z-10" />

              <motion.div
                className="absolute w-1 h-1 bg-red-400 rounded-full opacity-0 group-hover:opacity-100"
                animate={{
                  y: [0, -10, 0],
                  x: [-5, 5, -5],
                  scale: [0, 1, 0],
                }}
                transition={{ duration: 1.5, repeat: Infinity, delay: 0.1 }}
                style={{ top: "30%", left: "30%" }}
              />
              <motion.div
                className="absolute w-1 h-1 bg-rose-400 rounded-full opacity-0 group-hover:opacity-100"
                animate={{
                  y: [0, -8, 0],
                  x: [5, -5, 5],
                  scale: [0, 1, 0],
                }}
                transition={{ duration: 1.5, repeat: Infinity, delay: 0.3 }}
                style={{ top: "60%", right: "30%" }}
              />
            </motion.div>

            <motion.div
              className="flex items-center gap-2 text-sm bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl px-4 py-2.5 shadow-lg cursor-pointer border border-white/40 dark:border-gray-700/40 relative overflow-hidden group"
              onClick={onRegenerate}
              whileHover={{
                scale: 1.05,
                boxShadow: "0 8px 25px rgba(147,51,234,0.2)",
                transition: { duration: 0.2 },
              }}
              whileTap={{ scale: 0.95 }}
            >
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-purple-400/20 via-violet-400/20 to-indigo-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                animate={{
                  background: [
                    "linear-gradient(45deg, rgba(147,51,234,0.1), rgba(139,92,246,0.1), rgba(99,102,241,0.1))",
                    "linear-gradient(135deg, rgba(139,92,246,0.1), rgba(99,102,241,0.1), rgba(147,51,234,0.1))",
                    "linear-gradient(225deg, rgba(99,102,241,0.1), rgba(147,51,234,0.1), rgba(139,92,246,0.1))",
                  ],
                }}
                transition={{ duration: 2, repeat: Infinity }}
              />

              <motion.div
                animate={{ rotate: [0, 360] }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "linear",
                }}
                className="relative z-10"
              >
                <RefreshCw className="w-4 h-4 text-purple-600 dark:text-purple-400" />
              </motion.div>

              <span className="font-medium text-purple-600 dark:text-purple-400 relative z-10">
                {t("imagen.createNew")}
              </span>

              <motion.div
                className="absolute w-1 h-1 bg-purple-400 rounded-full opacity-0 group-hover:opacity-100"
                animate={{
                  y: [0, -8, 0],
                  x: [-3, 3, -3],
                  scale: [0, 1, 0],
                }}
                transition={{ duration: 1.2, repeat: Infinity, delay: 0.1 }}
                style={{ top: "25%", left: "25%" }}
              />
              <motion.div
                className="absolute w-1 h-1 bg-violet-400 rounded-full opacity-0 group-hover:opacity-100"
                animate={{
                  y: [0, -6, 0],
                  x: [3, -3, 3],
                  scale: [0, 1, 0],
                }}
                transition={{ duration: 1.2, repeat: Infinity, delay: 0.3 }}
                style={{ top: "65%", right: "25%" }}
              />
            </motion.div>

            {/* Restore button */}
            {isEnhanced && onRestore && (
              <motion.div
                className="flex items-center gap-2 text-sm bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl px-4 py-2.5 shadow-lg cursor-pointer border border-white/40 dark:border-gray-700/40 relative overflow-hidden group"
                onClick={onRestore}
                whileHover={{
                  scale: 1.05,
                  boxShadow: "0 8px 25px rgba(234,88,12,0.2)",
                }}
                whileTap={{ scale: 0.95 }}
                style={{ transform: "none" }} // Fix for hover issue
              >
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-orange-400/20 via-amber-400/20 to-yellow-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  animate={{
                    background: [
                      "linear-gradient(45deg, rgba(234,88,12,0.1), rgba(245,158,11,0.1), rgba(251,191,36,0.1))",
                      "linear-gradient(135deg, rgba(245,158,11,0.1), rgba(251,191,36,0.1), rgba(234,88,12,0.1))",
                      "linear-gradient(225deg, rgba(251,191,36,0.1), rgba(234,88,12,0.1), rgba(245,158,11,0.1))",
                    ],
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                  style={{ transform: "none" }} // Fix for gradient movement
                />

                <motion.div
                  animate={{ x: [-2, 2, -2] }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="relative z-10"
                  style={{ transform: "none" }} // Fix for icon movement
                >
                  <ArrowLeft className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                </motion.div>

                <span className="font-medium text-orange-600 dark:text-orange-400 relative z-10">
                  {t("imagen.restore")}
                </span>

                <motion.div
                  className="absolute w-1 h-1 bg-orange-400 rounded-full opacity-0 group-hover:opacity-100"
                  animate={{
                    y: [0, -8, 0],
                    x: [-3, 3, -3],
                    scale: [0, 1, 0],
                  }}
                  transition={{ duration: 1.2, repeat: Infinity, delay: 0.1 }}
                  style={{ top: "25%", left: "25%", transform: "none" }} // Fix for particle movement
                />
                <motion.div
                  className="absolute w-1 h-1 bg-amber-400 rounded-full opacity-0 group-hover:opacity-100"
                  animate={{
                    y: [0, -6, 0],
                    x: [3, -3, 3],
                    scale: [0, 1, 0],
                  }}
                  transition={{ duration: 1.2, repeat: Infinity, delay: 0.3 }}
                  style={{ top: "65%", right: "25%", transform: "none" }} // Fix for particle movement
                />
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
